// frontend/src/components/Common/CalendarView.jsx
// Generic month-view calendar — reusable for any date-based data.
//
// Props:
//   events: Array<{
//     id:       string | number   — unique key
//     title:    string            — main label shown on tile
//     date:     string            — ISO date 'YYYY-MM-DD'
//     color:    string            — hex / css color for the pill
//     badge?:   string            — small text shown after title (e.g. order no)
//     tooltip?: string            — hover tooltip
//     onClick:  () => void        — called when pill or modal link is clicked
//   }>
//   loading?:  bool
//   title?:    string             — page heading

import React, { useState, useMemo, useEffect } from 'react';
import { FiChevronLeft, FiChevronRight, FiCalendar, FiX, FiExternalLink } from 'react-icons/fi';
import './CalendarView.css';

const DAYS_PT   = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MONTHS_PT = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
];

const toKey = (d) => {
  const year  = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day   = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const buildGrid = (year, month) => {
  const first  = new Date(year, month, 1);
  const start  = new Date(first);
  start.setDate(first.getDate() - first.getDay());
  const cells  = [];
  const cursor = new Date(start);
  while (cells.length < 42) {
    cells.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return cells;
};

// ─── Day events modal ─────────────────────────────────────────────────────────
const DayModal = ({ date, events, onClose }) => {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const label = date.toLocaleDateString('pt-PT', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div className="cal-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="cal-modal">
        <div className="cal-modal-header">
          <div>
            <h3 className="cal-modal-title">
              <FiCalendar size={15} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              {label}
            </h3>
            <div className="cal-modal-count">{events.length} tarefa{events.length !== 1 ? 's' : ''}</div>
          </div>
          <button className="cal-modal-close" onClick={onClose} type="button">
            <FiX size={17} />
          </button>
        </div>

        <div className="cal-modal-body">
          {events.map((ev, i) => (
            <div key={`${ev.id}-${i}`} className="cal-modal-row">
              <span
                className="cal-modal-dot"
                style={{ background: ev.color }}
              />
              <div className="cal-modal-info">
                <span className="cal-modal-op">{ev.title}</span>
                {ev.badge && (
                  <span
                    className="cal-modal-badge"
                    style={{ background: ev.color + '22', color: ev.color }}
                  >
                    {ev.badge}
                  </span>
                )}
                {ev.tooltip && (
                  <span className="cal-modal-sub">
                    {/* tooltip is "op · order · area · customer · status" — skip first part (title) */}
                    {ev.tooltip.split(' · ').slice(1).join(' · ')}
                  </span>
                )}
              </div>
              <button
                className="cal-modal-link"
                title="Abrir pedido"
                onClick={() => { ev.onClick(); onClose(); }}
                type="button"
              >
                <FiExternalLink size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── CalendarView ─────────────────────────────────────────────────────────────
const CalendarView = ({ events = [], loading = false, title }) => {
  const today   = new Date();
  const [year,     setYear]     = useState(today.getFullYear());
  const [month,    setMonth]    = useState(today.getMonth());
  const [dayModal, setDayModal] = useState(null); // { date, events }

  const grid     = useMemo(() => buildGrid(year, month), [year, month]);
  const todayKey = toKey(today);

  const byDate = useMemo(() => {
    const map = {};
    for (const ev of events) {
      if (!ev.date) continue;
      const key = ev.date.slice(0, 10);
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    }
    return map;
  }, [events]);

  const prevMonth = () => {
    if (month === 0) { setYear(y => y - 1); setMonth(11); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setYear(y => y + 1); setMonth(0); }
    else setMonth(m => m + 1);
  };
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  return (
    <>
      <div className="cal-root">
        {/* ── Header ── */}
        <div className="cal-header">
          <div className="cal-header-left">
            {title && <h2 className="cal-title">{title}</h2>}
            <div className="cal-nav">
              <button className="cal-nav-btn" onClick={prevMonth} title="Mês anterior">
                <FiChevronLeft size={16}/>
              </button>
              <span className="cal-month-label">{MONTHS_PT[month]} {year}</span>
              <button className="cal-nav-btn" onClick={nextMonth} title="Próximo mês">
                <FiChevronRight size={16}/>
              </button>
            </div>
          </div>
          <button className="cal-today-btn" onClick={goToday}>
            <FiCalendar size={13}/> Hoje
          </button>
        </div>

        {/* ── Day-of-week labels ── */}
        <div className="cal-dow-row">
          {DAYS_PT.map(d => <div key={d} className="cal-dow">{d}</div>)}
        </div>

        {/* ── Grid ── */}
        {loading ? (
          <div className="cal-loading">A carregar…</div>
        ) : (
          <div className="cal-grid">
            {grid.map((date, i) => {
              const key       = toKey(date);
              const isToday   = key === todayKey;
              const inMonth   = date.getMonth() === month;
              const dayEvts   = byDate[key] || [];
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;
              const overflow  = dayEvts.length - 3;

              return (
                <div
                  key={i}
                  className={[
                    'cal-cell',
                    !inMonth  ? 'cal-cell--out'        : '',
                    isToday   ? 'cal-cell--today'      : '',
                    isWeekend ? 'cal-cell--weekend'    : '',
                    dayEvts.length > 0 ? 'cal-cell--has-events' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <div className="cal-day-num">
                    {isToday
                      ? <span className="cal-today-dot">{date.getDate()}</span>
                      : date.getDate()
                    }
                  </div>

                  <div className="cal-events">
                    {dayEvts.slice(0, 3).map((ev, ei) => (
                      <button
                        key={`${key}-${ei}`}
                        className="cal-event-pill"
                        title={ev.tooltip || ev.title}
                        onClick={ev.onClick}
                        style={{ '--ev-color': ev.color }}
                      >
                        <span className="cal-event-dot"/>
                        <span className="cal-event-text">{ev.title}</span>
                        {ev.badge && (
                          <span className="cal-event-badge"
                            style={{ background: ev.color + '22', color: ev.color }}>
                            {ev.badge}
                          </span>
                        )}
                      </button>
                    ))}

                    {overflow > 0 && (
                      <button
                        className="cal-overflow"
                        onClick={() => setDayModal({ date, events: dayEvts })}
                        type="button"
                      >
                        +{overflow} mais
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Day modal ── */}
      {dayModal && (
        <DayModal
          date={dayModal.date}
          events={dayModal.events}
          onClose={() => setDayModal(null)}
        />
      )}
    </>
  );
};

export default CalendarView;